import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { logger } from '@/lib/logger';
import { AvatarEditor } from './AvatarEditor';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  userName?: string;
  onUploadComplete: (url: string) => void;
  compact?: boolean; // Modo compacto para onboarding
}

export const AvatarUpload = ({ currentAvatarUrl, userName, onUploadComplete, compact = false }: AvatarUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentAvatarUrl || null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync preview with currentAvatarUrl when it changes
  useEffect(() => {
    setPreview(currentAvatarUrl || null);
  }, [currentAvatarUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB');
      return;
    }

    // Create preview and open editor
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result as string);
      setEditorOpen(true);
    };
    reader.readAsDataURL(file);

    // Clear input
    e.target.value = '';
  };

  const handleSaveEditedImage = async (croppedImageBlob: Blob) => {
    setUploading(true);

    try {
      // Compress edited image
      const compressedFile = await imageCompression(croppedImageBlob as File, {
        maxSizeMB: 0.5,
        useWebWorker: true,
      });

      // Create preview
      const previewUrl = URL.createObjectURL(compressedFile);
      setPreview(previewUrl);

      // Get user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload to Supabase Storage
      const fileName = `${user.id}/avatar-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, compressedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      onUploadComplete(publicUrl);
    } catch (error: any) {
      logger.error('Avatar upload error', error, {
        component: 'AvatarUpload',
        action: 'handleSaveEditedImage'
      });
      toast.error(error.message || 'Erro ao fazer upload');
      setPreview(currentAvatarUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (error) throw error;

      setPreview(null);
      toast.success('Avatar removido');
      onUploadComplete('');
    } catch (error: any) {
      toast.error('Erro ao remover avatar');
    }
  };

  const avatarSize = compact ? "w-24 h-24" : "w-28 h-28";
  const iconSize = compact ? "w-4 h-4" : "w-5 h-5";
  const buttonSize = compact ? "p-2.5" : "p-3";

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="relative group">
          <Avatar className={`${avatarSize} ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all`}>
            <AvatarImage src={preview || undefined} />
            <AvatarFallback className="text-2xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary font-bold">
              {userName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>

          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`absolute bottom-0 right-0 ${buttonSize} bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {uploading ? (
              <Loader2 className={`${iconSize} animate-spin`} />
            ) : (
              <Camera className={iconSize} />
            )}
          </button>

          {/* Remove button */}
          {preview && !uploading && (
            <button
              onClick={handleRemoveAvatar}
              className={`absolute top-0 right-0 ${buttonSize} bg-destructive text-destructive-foreground rounded-full shadow-lg opacity-0 group-hover:opacity-100 hover:scale-110 transition-all active:scale-95`}
            >
              <X className={iconSize} />
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {!compact && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Clique no ícone da câmera para editar
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              JPG, PNG ou GIF • Máximo 5MB
            </p>
          </div>
        )}
        
        {compact && (
          <p className="text-xs text-muted-foreground text-center">
            {preview ? "Toque na câmera para mudar" : "Adicione uma foto (opcional)"}
          </p>
        )}
      </div>

      {selectedImage && (
        <AvatarEditor
          imageSrc={selectedImage}
          open={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setSelectedImage(null);
          }}
          onSave={handleSaveEditedImage}
        />
      )}
    </>
  );
};
